import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { PusherContext } from '../pages/PusherContext.jsx'

/**
 * Confirmation states for the payment WebSocket flow
 * @type {Object}
 */
export const CONFIRMATION_STATE = {
  IDLE: 'idle',
  WAITING: 'waiting',
  CONFIRMED: 'confirmed',
  FAILED: 'failed',
  TIMEOUT: 'timeout',
}

/**
 * Error types from the backend confirmation-failed event
 * @type {Object}
 */
export const CONFIRMATION_ERROR_TYPES = {
  VALIDATION_ERROR: 'validation_error',
  DATABASE_ERROR: 'database_error',
  UNEXPECTED_ERROR: 'unexpected_error',
  // Stripe error types
  CARD_ERROR: 'card_error',
  API_ERROR: 'api_error',
  AUTHENTICATION_ERROR: 'authentication_error',
  INVALID_REQUEST_ERROR: 'invalid_request_error',
  RATE_LIMIT_ERROR: 'rate_limit_error',
}

/**
 * Default timeout for waiting for WebSocket confirmation (30 seconds)
 * @type {number}
 */
const DEFAULT_TIMEOUT_MS = 30000

/**
 * Hook for subscribing to registration payment confirmation via WebSocket
 * Listens for payment-confirmed and confirmation-failed events after Stripe payment
 *
 * @param {string|null} sessionId - The registration session ID to subscribe to
 * @returns {Object} Confirmation state and controls
 */
const useRegistrationPaymentWebSocket = (sessionId) => {
  const { client, isReady } = useContext(PusherContext) || {}

  // Confirmation state
  const [confirmationState, setConfirmationState] = useState(
    CONFIRMATION_STATE.IDLE
  )
  const [confirmedSession, setConfirmedSession] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [errorType, setErrorType] = useState(null)
  const [errorCode, setErrorCode] = useState(null)
  const [errorDeclineCode, setErrorDeclineCode] = useState(null)

  // Refs for cleanup
  const channelRef = useRef(null)
  const timeoutRef = useRef(null)

  /**
   * Cleanup function to clear timeout and reset refs
   */
  const cleanupTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  /**
   * Reset all confirmation state
   */
  const resetConfirmation = useCallback(() => {
    cleanupTimeout()
    setConfirmationState(CONFIRMATION_STATE.IDLE)
    setConfirmedSession(null)
    setErrorMessage(null)
    setErrorType(null)
    setErrorCode(null)
    setErrorDeclineCode(null)
  }, [cleanupTimeout])

  /**
   * Start waiting for payment confirmation via WebSocket
   * Sets up timeout fallback
   */
  const startWaitingForConfirmation = useCallback(() => {
    setConfirmationState(CONFIRMATION_STATE.WAITING)
    setErrorMessage(null)
    setErrorType(null)
    setErrorCode(null)
    setErrorDeclineCode(null)

    // Set up timeout fallback
    cleanupTimeout()
    timeoutRef.current = setTimeout(() => {
      setConfirmationState(CONFIRMATION_STATE.TIMEOUT)
      setErrorMessage(
        'Payment confirmation is taking longer than expected. Please refresh the page or contact support if the issue persists.'
      )
    }, DEFAULT_TIMEOUT_MS)
  }, [cleanupTimeout])

  // Subscribe to registration session channel
  useEffect(() => {
    if (!client || !isReady || !sessionId) {
      return
    }

    const channelName = `registration-session.${sessionId}`

    try {
      channelRef.current = client.subscribe(channelName)

      channelRef.current.bind('pusher:subscription_succeeded', () => {})

      channelRef.current.bind('pusher:subscription_error', () => {})

      // Listen for payment-confirmed event
      // Data format: { registration_session: { id, registration_step, ... } }
      channelRef.current.bind('payment-confirmed', (data) => {
        const sessionData = data?.registration_session
        cleanupTimeout()
        setConfirmedSession(sessionData)
        setConfirmationState(CONFIRMATION_STATE.CONFIRMED)
      })

      // Listen for confirmation-failed event
      // Data format from Stripe: { type, message, code, decline_code, ... }
      channelRef.current.bind('confirmation-failed', (data) => {
        // Extract error message - prioritize top-level message, fallback to nested
        const message =
          data?.message ||
          data?.payment_intent?.last_payment_error?.message ||
          'Payment confirmation failed. Please try again.'

        // Extract error type - Stripe uses 'type', backend may use 'error_type'
        const type =
          data?.type ||
          data?.error_type ||
          CONFIRMATION_ERROR_TYPES.UNEXPECTED_ERROR

        // Extract error codes for more granular handling
        const code =
          data?.code || data?.payment_intent?.last_payment_error?.code
        const declineCode =
          data?.decline_code ||
          data?.payment_intent?.last_payment_error?.decline_code

        cleanupTimeout()
        setErrorMessage(message)
        setErrorType(type)
        setErrorCode(code || null)
        setErrorDeclineCode(declineCode || null)
        setConfirmationState(CONFIRMATION_STATE.FAILED)
      })
    } catch {
      // Subscription error handled silently
    }

    return () => {
      cleanupTimeout()

      if (channelRef.current) {
        channelRef.current.unbind('payment-confirmed')
        channelRef.current.unbind('confirmation-failed')
        channelRef.current.unbind('pusher:subscription_succeeded')
        channelRef.current.unbind('pusher:subscription_error')
        channelRef.current = null
      }

      client.unsubscribe(channelName)
    }
  }, [client, isReady, sessionId, cleanupTimeout])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupTimeout()
    }
  }, [cleanupTimeout])

  return {
    confirmationState,
    confirmedSession,
    errorMessage,
    errorType,
    errorCode,
    errorDeclineCode,
    startWaitingForConfirmation,
    resetConfirmation,
    isWaiting: confirmationState === CONFIRMATION_STATE.WAITING,
    isConfirmed: confirmationState === CONFIRMATION_STATE.CONFIRMED,
    isFailed:
      confirmationState === CONFIRMATION_STATE.FAILED ||
      confirmationState === CONFIRMATION_STATE.TIMEOUT,
    isCardError: errorType === CONFIRMATION_ERROR_TYPES.CARD_ERROR,
  }
}

export default useRegistrationPaymentWebSocket
